import React, { useState, useRef } from 'react';
import { scopeHTML, buildSrcDoc } from '../../utils/sanitize.js';
import { sanitizeEmbeddedDocument } from '../../utils/sanitizeHtml';

const EmbeddedHtmlRenderer = ({ block, isEditMode, onBlockUpdate }) => {
  const [dragActive, setDragActive] = useState(false);
  const [showTitleDesc, setShowTitleDesc] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = (file) => {
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.html') && !file.name.endsWith('.htm') && !file.type.includes('html')) {
      alert('Please select an HTML file (.html or .htm)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('HTML file size must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const htmlContent = e.target.result;
      onBlockUpdate({
        ...block,
        content: htmlContent,
        fileName: file.name,
        fileSize: file.size,
        lastModified: new Date().toISOString(),
        title: block.title || '',
        description: block.description || ''
      });
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    handleFileUpload(file);
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    onBlockUpdate({
      ...block,
      content: '',
      fileName: null,
      fileSize: null,
      lastModified: null,
      title: '',
      description: ''
    });
  };

  const handleTitleChange = (e) => {
    onBlockUpdate({
      ...block,
      title: e.target.value
    });
  };

  const handleDescriptionChange = (e) => {
    onBlockUpdate({
      ...block,
      description: e.target.value
    });
  };

  // If no content, show upload interface
  if (!block.content || block.content.trim() === '') {
    return (
      <div
        className={`embedded-html-upload border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="flex flex-col items-center gap-4">
          <div className="text-6xl">💻</div>
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Embedded HTML Layout</h3>
            <p className="text-sm text-gray-600 mb-4">
              {dragActive
                ? 'Drop HTML file here'
                : 'Upload an HTML file to embed it in this slide'}
            </p>
          </div>
          {isEditMode && (
            <>
              <button
                onClick={handleChooseFile}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Choose HTML File
              </button>
              <p className="text-xs text-gray-500">
                or drag and drop an HTML file here
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".html,.htm,text/html"
                onChange={handleFileInput}
                className="hidden"
              />
            </>
          )}
        </div>
      </div>
    );
  }

  // Render the embedded HTML content
  const sanitized = sanitizeEmbeddedDocument(block.content);
  const scoped = scopeHTML(sanitized, `embedded-html-${block.id}`);
  const srcdoc = buildSrcDoc(scoped.html);

  return (
    <div className="embedded-html-container" onClick={(e) => e.stopPropagation()}>
      {/* Title and Description fields - shown in edit mode or if they have values */}
      {(isEditMode || block.title || block.description) && (
        <div className="mb-4">
          {isEditMode && (
            <>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title (optional)
                </label>
                <input
                  type="text"
                  value={block.title || ''}
                  onChange={handleTitleChange}
                  placeholder="Enter a title for this embedded content"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={block.description || ''}
                  onChange={handleDescriptionChange}
                  placeholder="Enter a description for this embedded content"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>
            </>
          )}
          {!isEditMode && (block.title || block.description) && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              {block.title && (
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{block.title}</h3>
              )}
              {block.description && (
                <p className="text-sm text-gray-600">{block.description}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Header with file info and controls */}
      {isEditMode && (
        <div className="flex items-center justify-between bg-gray-100 px-4 py-2 rounded-t-lg border border-gray-300">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="font-medium">📄 {block.fileName || 'Embedded HTML'}</span>
            {block.fileSize && (
              <span className="text-gray-500">
                ({(block.fileSize / 1024).toFixed(1)} KB)
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleChooseFile}
              className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              title="Replace HTML file"
            >
              Replace
            </button>
            <button
              onClick={handleRemove}
              className="px-3 py-1 text-sm bg-red-100 text-red-700 border border-red-300 rounded hover:bg-red-200 transition-colors"
              title="Remove HTML file"
            >
              Remove
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.htm,text/html"
            onChange={handleFileInput}
            className="hidden"
          />
        </div>
      )}

      {/* Sandboxed iframe for HTML content */}
      <div
        className={`embedded-html-preview border border-gray-300 ${
          isEditMode ? 'rounded-b-lg' : 'rounded-lg'
        } overflow-hidden`}
        style={{ minHeight: '400px' }}
      >
        <iframe
          srcDoc={srcdoc}
          // Scripts run so an uploaded layout behaves like a real page, but
          // WITHOUT allow-same-origin: the two together put the frame back in
          // the parent's origin and void the sandbox, giving uploaded HTML
          // read access to every lesson in localStorage. This frame is a fixed
          // min-height with its own scrolling and never measured the parent
          // document, so nothing here depended on same-origin access.
          sandbox="allow-scripts allow-forms"
          style={{
            width: '100%',
            minHeight: '400px',
            border: 'none',
            display: 'block'
          }}
          title={block.title || `Embedded HTML: ${block.fileName || 'content'}`}
        />
      </div>
    </div>
  );
};

export default EmbeddedHtmlRenderer;
